import { pathOr } from "ramda";

import { theme } from "@sisuwellness/web-components";
import {
    isMobileView,
    createLegends,
    calculateMinMax,
    createYGridValues,
    flattenPointValues,
    createPlottableDataPoints,
    calculateMeanValue
} from "components/Templates/TrendChart/Line/utils";
import { METRIC_INDICATOR } from "utilities/metrics/mapIndicators";
import calculatePointColor from "utilities/trend-chart/calculateGuldeline";
import { HEART_RATE } from "constants/trend-charts";
import i18n from "../../../../i18n.js";

import AreaLayer from "./AreaLayer";

const defaultColor = theme.portalColor.hpPrimaryPurple.hex;

const METRIC_FIELDS = {
    rounded: true,
    key: ["hr"],
    additionalKeys: ["rating", "unit"]
};

/**
 * Update data with rating
 * @param {object} data
 * @return {{ rating: { key: string, label: string, ranges: object } }}
 */
const hrRatingCalc = data => {
    const { guideline, currentValue } = calculatePointColor(HEART_RATE, data);

    return {
        ...data,
        unit: i18n.t("units.bpm"),
        hr: Number(currentValue),
        rating: { key: guideline?.key, label: guideline?.label, ranges: guideline?.constructor?.ranges }
    };
};

const plot = {
    // unit: () => "Heart Rate (bpm)",
    unit: () => `${i18n.t("words.heart_rate")} (${i18n.t("units.bpm")})`,

    color: ({ rating } = {}) => theme.guidelines.SiSUHeartRate[(rating?.key)]?.sisuPortal?.hex ?? defaultColor,

    dataPoints: (healthChecks = [], settings) => {
        const points = createPlottableDataPoints(data => hrRatingCalc(data, settings))(
            healthChecks,
            METRIC_FIELDS.key,
            METRIC_FIELDS.additionalKeys
        );

        return points;
    },

    yRange: (points = []) => {
        const { maximum } = calculateMinMax(flattenPointValues(points, "y"));
        const ranges = createYGridValues(30, Math.max(80, maximum), { parts: 5, rounded: METRIC_FIELDS.rounded });

        return {
            min: 30,
            yScaleRanges: ranges,
            max: Math.max(80, ranges[ranges.length - 1])
        };
    },

    legends: (points = []) =>
        createLegends(
            pathOr({}, [0, "data", 0, "rating", "ranges"], points),
            METRIC_INDICATOR.HEART_RATE.themeKey,
            "words.heart_rate"
        ),

    historyProps: (filteredPoints = [], latestHealthCheckData = {}) => {
        const { hr } = latestHealthCheckData;
        const { rating } = hrRatingCalc({ hr });

        const meanHr = calculateMeanValue("hr", filteredPoints[0]);

        const { rating: meanRating } = hrRatingCalc({ hr: meanHr });

        return {
            unit: i18n.t("units.bpm"),
            value: hr,
            meanValue: Math.round(meanHr),
            valueColor: theme.guidelines.SiSUHeartRate[(rating?.key)]?.sisuPortal?.hex,
            meanColor: theme.guidelines.SiSUHeartRate[(meanRating?.key)]?.sisuPortal?.hex
        };
    },

    margins: width => ({ right: isMobileView(width) ? 16 : 80, left: isMobileView(width) ? 50 : 80 })
};

export default { plottingUtility: plot, AreaLayer };
